import { Box, useTheme } from "@mui/material";
import { Provider } from "jotai";
import { useMemo } from "react";
import type { ChatResultItem } from "shared/types";
import { DisabledStatus } from "shared/data";
import { usePinnedStore } from "controller/store/pinned-chat/pinnedChatStore";
import { createPinnedChatStore } from "../store";
import { PinnedChatPanel } from "./PinnedChatPanel";

interface ChatBoxProps {
  onMinimize: () => void;
  isOpen: boolean;
  serviceType: "ticket" | "task";
  serviceId: string;
  mqttMessage: ChatResultItem | null;
}

export function ChatBox({
  onMinimize,
  isOpen,
  serviceType,
  serviceId,
  mqttMessage,
}: ChatBoxProps) {
  const theme = useTheme();
  const pinnedChat = usePinnedStore((state) => state.pinnedChat[serviceId]);

  const isDisabled = useMemo(
    () => DisabledStatus.includes(pinnedChat?.serviceStatus),
    [pinnedChat?.serviceStatus],
  );

  // Scoped Jotai store -- isolates all atom reads/writes for this pinned chat
  // instance from the global store and from other pinned chats.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: recreate store when serviceId changes
  const scopedStore = useMemo(() => createPinnedChatStore(), [serviceId]);

  return (
    <Box
      sx={{
        width: isOpen ? "340px" : "0px",
        height: isOpen ? "calc(100vh - 120px)" : "0px",
        maxHeight: "500px",
        border: isOpen ? `1px solid ${theme.palette.darkBg.low}` : "none",
        boxShadow: "0px 0px 8px 0px #00000021, 0px 0px 6px 0px #0000000F",
        backgroundColor: theme.palette.neutral["005"],
        borderRadius: "8px",
        overflow: "hidden",
        transition: isOpen
          ? "width 0.2s ease-in-out , height 0.2s ease-in-out"
          : "width 0.1s ease-in-out , height 0.05s ease-in-out",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {isOpen && (
        <Provider store={scopedStore}>
          <PinnedChatPanel
            serviceType={serviceType}
            serviceId={serviceId}
            isDisabled={isDisabled}
            mqttMessage={mqttMessage}
            onMinimize={onMinimize}
          />
        </Provider>
      )}
    </Box>
  );
}
